/**
 * Frontend Audit Tracker Hook
 *
 * Provides a reusable hook for tracking audit events from any frontend application
 * (core, admin, embed, kiosk, mobile, etc.)
 *
 * Usage:
 * ```typescript
 * const { trackSettingChange, trackEvent, createMutationCallbacks } = useAuditTracker({
 *   corporateId: 'corp_123',
 *   userId: 'user_456',
 *   userEmail: 'user@example.com',
 *   userFirstName: 'John',
 *   userLastName: 'Doe',
 * })
 *
 * // Track a setting change
 * await trackSettingChange({
 *   settingName: 'enableClientsToSignUp',
 *   settingDisplayName: 'Enable clients to sign up',
 *   oldValue: false,
 *   newValue: true,
 *   siteId: 'site_123',
 *   siteName: 'Downtown Branch',
 * })
 *
 * // Track a generic event
 * await trackEvent({
 *   action: AuditAction.MEMBER_UPDATED,
 *   resource: AuditResource.MEMBER,
 *   resourceId: 'member_456',
 * })
 *
 * // Use callback-based tracking for atomic mutation tracking
 * const { onSuccess, onError } = createMutationCallbacks({
 *   action: AuditAction.SITE_SETTING_UPDATED,
 *   resource: AuditResource.SETTING,
 *   settingName: 'enableClientsToSignUp',
 *   getOldValue: () => previousValue,
 *   getNewValue: () => form.values.enableClientsToSignUp,
 * })
 *
 * await mutation({ args, onSuccess, onError })
 * ```
 */

import { useCallback, useMemo } from 'react'
import { resolved, mutation, AuditDeviceType as GQtyAuditDeviceType } from '../services/gqty'
import {
  AuditAction,
  AuditResource,
  AuditVisibility,
  AuditSeverity,
  AuditSource,
  AuditPlatform,
  AuditDeviceType,
  type CreateAuditEventInput,
} from '@hapana/shared/schemas/audit'

export { AuditAction, AuditResource, AuditVisibility, AuditSeverity, AuditSource, AuditPlatform }

function toUpperCaseDeviceType(
  deviceType: AuditDeviceType | null | undefined
): GQtyAuditDeviceType | undefined {
  if (!deviceType) {
    return undefined
  }
  return deviceType.toUpperCase() as GQtyAuditDeviceType
}

export interface AuditTrackerConfig {
  /** Corporate/brand ID - required for all audit events */
  corporateId: string
  /** Corporate slug for BigQuery dataset naming (optional) */
  corporateSlug?: string
  /** Corporate/brand name (denormalized for analytics) */
  corporateName?: string
  /** Current user ID */
  userId?: string
  /** Current user email */
  userEmail?: string
  /** Current user first name */
  userFirstName?: string
  /** Current user last name */
  userLastName?: string
  /** Application source */
  source?: AuditSource
  /** Site context */
  siteId?: string
  siteName?: string
  /** Session ID for grouping events */
  sessionId?: string

  // User context (non-PII)
  /** User email domain (e.g., "hapana.com") */
  emailDomain?: string
  /** User roles at time of action */
  userRoles?: string[]

  // Device context (non-PII)
  /** Device type */
  deviceType?: AuditDeviceType
  /** Browser name */
  browserName?: string
  /** Browser version */
  browserVersion?: string
  /** OS name */
  osName?: string
  /** OS version */
  osVersion?: string
  /** Screen width in pixels */
  screenWidth?: number
  /** Screen height in pixels */
  screenHeight?: number
  /** Browser language */
  language?: string
  /** User timezone */
  timezone?: string
}

/**
 * Options for creating mutation callbacks
 */
export interface MutationCallbackOptions {
  /** The audit action type */
  action: AuditAction
  /** The resource being affected */
  resource: AuditResource
  /** ID of the resource being affected */
  resourceId?: string
  /** Site ID (optional, for site-scoped events) */
  siteId?: string
  /** Site name (optional, denormalized) */
  siteName?: string
  /** Visibility level of the event */
  visibility?: AuditVisibility
  /** Severity level of the event */
  severity?: AuditSeverity

  // For setting changes
  /** Setting key/name */
  settingName?: string
  /** Human-readable setting name */
  settingDisplayName?: string
  /** Function to get the old value */
  getOldValue?: () => unknown
  /** Function to get the new value */
  getNewValue?: () => unknown

  /** Optional custom summary generator */
  getSummary?: (result: unknown) => string

  /** Change type for analytics */
  changeType?: 'create' | 'update' | 'delete'

  /** Feature ID from feature registry */
  featureId?: string
  /** Feature category for analytics */
  featureCategory?: string

  corporateId?: string
}

/**
 * Callbacks returned by createMutationCallbacks
 */
export interface MutationCallbacks<TData = unknown, TError = Error> {
  onSuccess: (data: TData) => void
  onError: (error: TError) => void
}

export interface TrackSettingChangeOptions {
  /** The setting key/name being changed */
  settingName: string
  /** Human-readable display name for the setting */
  settingDisplayName?: string
  /** Previous value before the change */
  oldValue: unknown
  /** New value after the change */
  newValue: unknown
  /** Optional site ID if this is a site-level setting */
  siteId?: string
  /** Optional site name (denormalized for display) */
  siteName?: string
  /** Category of the setting (e.g., 'operations', 'configuration') */
  settingCategory?: string
  /** Page/section where the setting was changed */
  pageName?: string
}

export interface TrackEventOptions {
  /** The audit action type */
  action: AuditAction
  /** The resource being affected */
  resource: AuditResource
  /** ID of the resource being affected */
  resourceId?: string
  /** Site ID (optional, for site-scoped events) */
  siteId?: string
  /** Site name (optional, denormalized) */
  siteName?: string
  /** Visibility level of the event */
  visibility?: AuditVisibility
  /** Severity level of the event */
  severity?: AuditSeverity
  /** Event details */
  details?: {
    summary?: string
    changes?: Array<{
      field: string
      oldValue?: string | null
      newValue?: string | null
      displayName?: string
    }>
    reason?: string
    metadataJson?: string
  }
  /** ID of an affected user (e.g., the user being edited) */
  affectedUserId?: string
  /** ID of a member being affected */
  memberId?: string
  /** Page URL where the action occurred */
  pageUrl?: string
  /** Page name where the action occurred */
  pageName?: string
}

export interface AuditTrackerReturn {
  trackSettingChange: (options: TrackSettingChangeOptions) => void
  trackEvent: (options: TrackEventOptions) => void
  createMutationCallbacks: <TData = unknown, TError = Error>(
    options: MutationCallbackOptions
  ) => MutationCallbacks<TData, TError>
}

/**
 * Hook for tracking audit events from frontend applications
 *
 * This hook provides methods to track various types of audit events
 * that will be sent to the audit service via the GraphQL gateway.
 *
 * @param config - Configuration including corporateId, user info, etc.
 */
type AuditMutationArgs = Omit<
  CreateAuditEventInput,
  'action' | 'resource' | 'severity' | 'source' | 'visibility'
> & {
  action: AuditAction
  resource: AuditResource
  severity: AuditSeverity
  source: AuditSource
  visibility: AuditVisibility
  pageName?: string
}

export function useAuditTracker(config: AuditTrackerConfig): AuditTrackerReturn {
  const {
    corporateId,
    corporateSlug,
    corporateName,
    userId,
    userEmail,
    userFirstName,
    userLastName,
    // User context (non-PII)
    emailDomain,
    userRoles,
    // Device context (non-PII)
    deviceType,
    browserName,
    browserVersion,
    osName,
    osVersion,
    screenWidth,
    screenHeight,
    language,
    timezone,
  } = config

  const deviceContext = useMemo(
    () => ({
      emailDomain,
      userRoles,
      deviceType: deviceType as AuditDeviceType | undefined,
      browserName,
      browserVersion,
      osName,
      osVersion,
      screenWidth,
      screenHeight,
      language,
      timezone,
    }),
    [
      emailDomain,
      userRoles,
      deviceType,
      browserName,
      browserVersion,
      osName,
      osVersion,
      screenWidth,
      screenHeight,
      language,
      timezone,
    ]
  )

  const createAuditEventMutation = useCallback((options: { args: AuditMutationArgs }) => {
    const args = options.args
    return resolved(
      () => {
        const result = mutation.createAuditEvent({
          input: {
            action: args.action,
            resource: args.resource,
            corporateId: args.corporateId,
            severity: args.severity,
            source: args.source,
            visibility: args.visibility,
            resourceId: args.resourceId,
            siteId: args.siteId,
            siteName: args.siteName,
            userId: args.userId,
            sessionId: args.sessionId,
            corporateSlug: args.corporateSlug,
            corporateName: args.corporateName,
            userEmail: args.userEmail,
            userFirstName: args.userFirstName,
            userLastName: args.userLastName,
            pageName: args.pageName,
            pageUrl: args.pageUrl,
            emailDomain: args.emailDomain,
            userRoles: args.userRoles,
            deviceType: toUpperCaseDeviceType(args.deviceType),
            browserName: args.browserName,
            browserVersion: args.browserVersion,
            osName: args.osName,
            osVersion: args.osVersion,
            screenWidth: args.screenWidth,
            screenHeight: args.screenHeight,
            language: args.language,
            timezone: args.timezone,
            details: args.details
              ? {
                  previousValueJson: args.details.previousValueJson,
                  newValueJson: args.details.newValueJson,
                  summary: args.details.summary,
                  reason: args.details.reason,
                  changes: args.details.changes?.map((c) => ({
                    field: c.field,
                    oldValue: c.oldValue,
                    newValue: c.newValue,
                    displayName: c.displayName,
                    fieldType: c.fieldType,
                  })),
                  metadataJson: args.details.metadataJson,
                  contextJson: args.details.contextJson,
                }
              : undefined,
          },
        })
        if (result) {
          void result.id
          void result.timestamp
        }
        return result
      },
      { noCache: true }
    )
  }, [])

  /**
   * Track a setting change event
   *
   * Automatically determines whether this is a site or brand-level setting
   * based on whether siteId is provided.
   */
  const trackSettingChange = useCallback(
    (options: TrackSettingChangeOptions) => {
      // Don't track if we don't have the required corporateId
      if (!corporateId) {
        console.warn('[AuditTracker] Cannot track event: corporateId is required')
        return
      }

      const {
        settingName,
        settingDisplayName,
        oldValue,
        newValue,
        siteId,
        siteName,
        settingCategory,
        pageName,
      } = options

      // Determine action type based on whether this is a site-level setting
      const action = siteId ? AuditAction.SITE_SETTING_UPDATED : AuditAction.BRAND_SETTING_UPDATED

      // Format values for display
      const formatValue = (val: unknown): string => {
        if (val === null || val === undefined) {
          return 'null'
        }
        if (typeof val === 'boolean') {
          return val ? 'Enabled' : 'Disabled'
        }
        if (typeof val === 'object') {
          return JSON.stringify(val)
        }
        return String(val)
      }

      // For complex values (arrays/objects), generate a concise summary rather than
      // dumping raw JSON — the full from/to is available in the detail changes view.
      const formatSummaryValue = (val: unknown): string => {
        if (val === null || val === undefined) {
          return 'empty'
        }
        if (typeof val === 'boolean') {
          return val ? 'Enabled' : 'Disabled'
        }
        if (Array.isArray(val)) {
          return `${val.length} item${val.length !== 1 ? 's' : ''}`
        }
        if (typeof val === 'object') {
          return 'complex value'
        }
        return String(val)
      }

      const summary = `${settingDisplayName || settingName} changed from "${formatSummaryValue(oldValue)}" to "${formatSummaryValue(newValue)}"`

      createAuditEventMutation({
        args: {
          action,
          resource: AuditResource.SETTING,
          corporateId,
          severity: AuditSeverity.INFO,
          source: AuditSource.CORE_WEB,
          visibility: AuditVisibility.INTERNAL,
          resourceId: settingName,
          siteId: siteId || undefined,
          siteName,
          userId: userId || undefined,
          corporateSlug,
          corporateName,
          userEmail,
          userFirstName,
          userLastName,
          pageName:
            pageName || (typeof window !== 'undefined' ? window.location.pathname : undefined),
          pageUrl: typeof window !== 'undefined' ? window.location.pathname : undefined,
          ...deviceContext,
          details: {
            summary,
            changes: [
              {
                field: settingName,
                oldValue: formatValue(oldValue),
                newValue: formatValue(newValue),
                displayName: settingDisplayName || settingName,
                fieldType: 'setting',
              },
            ],
            metadataJson: JSON.stringify({
              category: settingCategory,
            }),
          },
        },
      }).catch((error) => {
        console.warn('[AuditTracker] Failed to track setting change:', error)
      })
    },
    [
      createAuditEventMutation,
      corporateId,
      corporateSlug,
      corporateName,
      userId,
      userEmail,
      userFirstName,
      userLastName,
      deviceContext,
    ]
  )

  /**
   * Track a generic audit event
   *
   * Provides full control over the audit event payload.
   */
  const trackEvent = useCallback(
    (options: TrackEventOptions) => {
      // Don't track if we don't have the required corporateId
      if (!corporateId) {
        console.warn('[AuditTracker] Cannot track event: corporateId is required')
        return
      }

      const {
        action,
        resource,
        resourceId,
        siteId,
        siteName,
        visibility = AuditVisibility.INTERNAL,
        severity = AuditSeverity.INFO,
        details,
        affectedUserId,
        memberId,
        pageUrl,
        pageName,
      } = options

      createAuditEventMutation({
        args: {
          action,
          resource,
          corporateId,
          severity,
          source: AuditSource.CORE_WEB,
          visibility,
          resourceId: resourceId || undefined,
          siteId: siteId || undefined,
          siteName,
          userId: userId || undefined,
          corporateSlug,
          corporateName,
          userEmail,
          userFirstName,
          userLastName,
          pageName,
          pageUrl:
            pageUrl || (typeof window !== 'undefined' ? window.location.pathname : undefined),
          ...deviceContext,
          details: {
            summary: details?.summary,
            reason: details?.reason,
            changes: details?.changes?.map((c) => ({
              field: c.field,
              oldValue: c.oldValue,
              newValue: c.newValue,
              displayName: c.displayName,
              fieldType: 'change',
            })),
            metadataJson: JSON.stringify({
              affectedUserId,
              memberId,
            }),
          },
        },
      }).catch((error) => {
        console.warn('[AuditTracker] Failed to track event:', error)
      })
    },
    [
      createAuditEventMutation,
      corporateId,
      corporateSlug,
      corporateName,
      userId,
      userEmail,
      userFirstName,
      userLastName,
      deviceContext,
    ]
  )

  /**
   * Create onSuccess/onError callbacks for atomic mutation tracking
   *
   * Use these callbacks with GQty mutations to ensure audit events are only
   * tracked when the mutation actually succeeds or fails. This provides
   * truly atomic audit tracking.
   *
   * NOTE: All audit events are sent to the Audit Service, which handles both:
   * - BigQuery persistence (for compliance and full PII audit trail)
   * - PostHog analytics (server-side, with PII redacted)
   *
   * Client-side PostHog is NOT used for audit events - only for feature flags,
   * error tracking, and session recordings.
   *
   * @example
   * ```typescript
   * const { onSuccess, onError } = createMutationCallbacks({
   *   action: AuditAction.SITE_SETTING_UPDATED,
   *   resource: AuditResource.SETTING,
   *   settingName: 'enableClientsToSignUp',
   *   getOldValue: () => previousValue,
   *   getNewValue: () => form.values.enableClientsToSignUp,
   * })
   *
   * await mutation({ args: { input }, onSuccess, onError })
   * ```
   */
  const createMutationCallbacks = useCallback(
    <TData = unknown, TError = Error>(
      options: MutationCallbackOptions
    ): MutationCallbacks<TData, TError> => {
      const {
        action,
        resource,
        resourceId,
        siteId: optionsSiteId,
        siteName: optionsSiteName,
        visibility = AuditVisibility.INTERNAL,
        severity = AuditSeverity.INFO,
        settingName,
        settingDisplayName,
        getOldValue,
        getNewValue,
        getSummary,
        corporateId: optionsCorporateId,
      } = options

      const effectiveCorporateId = optionsCorporateId ?? corporateId
      // Use provided siteId/siteName or fall back to config
      const effectiveSiteId = optionsSiteId ?? config.siteId
      const effectiveSiteName = optionsSiteName ?? config.siteName
      const effectiveSource = config.source ?? AuditSource.CORE_WEB

      // Format values for display (full JSON for the from/to detail view)
      const formatValue = (val: unknown): string => {
        if (val === null || val === undefined) {
          return 'null'
        }
        if (typeof val === 'boolean') {
          return val ? 'Enabled' : 'Disabled'
        }
        if (typeof val === 'object') {
          return JSON.stringify(val)
        }
        return String(val)
      }

      return {
        onSuccess: (data: TData) => {
          // Don't track if we don't have the required corporateId
          if (!effectiveCorporateId) {
            console.warn('[AuditTracker] Cannot track event: corporateId is required')
            return
          }

          let changes:
            | Array<{
                field: string
                oldValue: string
                newValue: string
                displayName?: string
                fieldType?: string
              }>
            | undefined
          let summary: string | undefined
          if (settingName) {
            const oldValue = getOldValue?.()
            const newValue = getNewValue?.()
            summary =
              getSummary?.(data) ||
              `${settingDisplayName || settingName} changed from "${formatValue(oldValue)}" to "${formatValue(newValue)}"`

            changes = [
              {
                field: settingName,
                oldValue: formatValue(oldValue),
                newValue: formatValue(newValue),
                displayName: settingDisplayName,
                fieldType: 'setting',
              },
            ]
          }

          createAuditEventMutation({
            args: {
              action,
              resource,
              corporateId: effectiveCorporateId,
              severity,
              source: effectiveSource,
              visibility,
              resourceId: resourceId || settingName || undefined,
              siteId: effectiveSiteId || undefined,
              siteName: effectiveSiteName,
              userId: userId || undefined,
              corporateSlug,
              corporateName,
              userEmail,
              userFirstName,
              userLastName,
              pageUrl: typeof window !== 'undefined' ? window.location.pathname : undefined,
              ...deviceContext,
              details: {
                summary,
                changes,
              },
            },
          }).catch((auditError) => {
            console.warn(
              '[AuditTracker] Audit tracking failed after successful mutation:',
              auditError
            )
          })
        },

        onError: (error: TError) => {
          // Failed mutations are NOT recorded in the audit trail
          // Only log for debugging purposes
          console.warn('[AuditTracker] Mutation failed:', {
            action,
            resource,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        },
      }
    },
    [
      createAuditEventMutation,
      corporateId,
      corporateSlug,
      corporateName,
      userId,
      userEmail,
      userFirstName,
      userLastName,
      config.siteId,
      config.siteName,
      config.source,
      deviceContext,
    ]
  )

  return {
    trackSettingChange,
    trackEvent,
    createMutationCallbacks,
  }
}

export default useAuditTracker
